import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/lib/supabaseClient";

interface ScrapeLog {
  id: string;
  started_at: string;
  finished_at: string | null;
  source: string;
  articles_found: number;
  articles_inserted: number;
  articles_auto_published: number;
  articles_skipped: number;
  errors: string[];
  status: "running" | "success" | "error";
}

export default function AdminLogsTab() {
  const [logs, setLogs] = useState<ScrapeLog[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await supabase
        .from("scrape_logs")
        .select("*")
        .order("started_at", { ascending: false })
        .limit(20);
      setLogs((data ?? []) as ScrapeLog[]);
    } catch {
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  // Prochain dimanche à minuit UTC
  const getNextSunday = () => {
    const now = new Date();
    const day = now.getUTCDay();
    const daysUntilSunday = day === 0 ? 7 : 7 - day;
    const next = new Date(now);
    next.setUTCDate(now.getUTCDate() + daysUntilSunday);
    next.setUTCHours(0, 0, 0, 0);
    return next;
  };

  const nextSunday = getNextSunday();
  const diffMs = nextSunday.getTime() - Date.now();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const diffHours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));

  const statusConfig = {
    running:  { color: "text-amber-600 bg-amber-50 border-amber-200",  icon: "ri-loader-4-line animate-spin", label: "En cours" },
    success:  { color: "text-green-600 bg-green-50 border-green-200",  icon: "ri-checkbox-circle-line",       label: "Succès" },
    error:    { color: "text-red-500 bg-red-50 border-red-200",        icon: "ri-error-warning-line",         label: "Erreur" },
  };

  return (
    <div>
      {/* Prochain scraping */}
      <div className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-2xl p-5 mb-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-orange-500/20 flex-shrink-0">
            <i className="ri-calendar-schedule-line text-orange-400 text-xl"></i>
          </div>
          <div>
            <p className="text-white font-semibold text-sm">Prochain scraping automatique</p>
            <p className="text-gray-400 text-xs mt-0.5">
              Chaque dimanche à 00:00 UTC — dans{" "}
              <span className="text-orange-400 font-semibold">{diffDays}j {diffHours}h</span>
              {" "}({nextSunday.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })})
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 text-xs text-gray-400 bg-white/5 px-4 py-2 rounded-full border border-white/10">
          <i className="ri-settings-3-line text-orange-400"></i>
          <span>Cron : <code className="text-orange-300">0 0 * * 0</code></span>
        </div>
      </div>

      {/* Sources RSS actives */}
      <div className="bg-white rounded-2xl border border-gray-100 p-5 mb-6">
        <h3 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
          <i className="ri-rss-line text-orange-500"></i>Sources actives
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { name: "Jeune Afrique", icon: "ri-global-line" },
            { name: "Agence Ecofin", icon: "ri-bar-chart-line" },
            { name: "Financial Afrik", icon: "ri-funds-line" },
            { name: "Le Point Afrique", icon: "ri-newspaper-line" },
            { name: "RFI Afrique", icon: "ri-radio-line" },
            { name: "Le360 Afrique", icon: "ri-earth-line" },
            { name: "Club Entrepreneurs", icon: "ri-team-line" },
            { name: "NewsAPI", icon: "ri-search-eye-line" },
          ].map((s) => (
            <div key={s.name} className="flex items-center gap-2 bg-gray-50 rounded-xl px-3 py-2">
              <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                <i className={`${s.icon} text-orange-500 text-sm`}></i>
              </div>
              <span className="text-xs text-gray-700 font-medium truncate">{s.name}</span>
              <i className="ri-checkbox-circle-fill text-green-500 text-xs ml-auto flex-shrink-0"></i>
            </div>
          ))}
        </div>
      </div>

      {/* Historique des logs */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-bold text-gray-900">Historique des scrapings</h3>
        <button
          onClick={fetchLogs}
          className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 text-gray-600 transition-colors cursor-pointer"
        >
          <i className="ri-refresh-line text-sm"></i>
        </button>
      </div>

      {loading ? (
        <div className="flex flex-col gap-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="bg-white rounded-2xl border border-gray-100 h-20 animate-pulse"></div>
          ))}
        </div>
      ) : logs.length === 0 ? (
        <div className="text-center py-16 text-gray-400 bg-white rounded-2xl border border-gray-100">
          <i className="ri-history-line text-5xl mb-4 block"></i>
          <p className="font-medium text-sm">Aucun scraping effectué pour l'instant</p>
          <p className="text-xs mt-1">Lancez un scraping manuel depuis le panel pour voir les logs ici</p>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {logs.map((log) => {
            const cfg = statusConfig[log.status] ?? statusConfig.success;
            const duration = log.finished_at
              ? Math.round((new Date(log.finished_at).getTime() - new Date(log.started_at).getTime()) / 1000)
              : null;
            return (
              <div key={log.id} className="bg-white rounded-2xl border border-gray-100 p-4">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full border ${cfg.color}`}>
                      <i className={cfg.icon}></i>{cfg.label}
                    </span>
                    <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${
                      log.source === "cron" ? "bg-orange-50 text-orange-600" : "bg-gray-100 text-gray-600"
                    }`}>
                      {log.source === "cron" ? "🕐 Automatique" : "👤 Manuel"}
                    </span>
                    {duration !== null && (
                      <span className="text-xs text-gray-400">{duration}s</span>
                    )}
                  </div>
                  <span className="text-xs text-gray-400">
                    {new Date(log.started_at).toLocaleDateString("fr-FR", {
                      day: "numeric", month: "short", year: "numeric",
                      hour: "2-digit", minute: "2-digit"
                    })}
                  </span>
                </div>

                {log.status !== "running" && (
                  <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div className="text-center bg-gray-50 rounded-xl py-2">
                      <p className="text-lg font-bold text-gray-900">{log.articles_found}</p>
                      <p className="text-xs text-gray-500">Trouvés</p>
                    </div>
                    <div className="text-center bg-green-50 rounded-xl py-2">
                      <p className="text-lg font-bold text-green-600">{log.articles_inserted}</p>
                      <p className="text-xs text-green-500">Insérés</p>
                    </div>
                    <div className="text-center bg-orange-50 rounded-xl py-2">
                      <p className="text-lg font-bold text-orange-500">{log.articles_auto_published}</p>
                      <p className="text-xs text-orange-400">Auto-publiés</p>
                    </div>
                    <div className="text-center bg-gray-50 rounded-xl py-2">
                      <p className="text-lg font-bold text-gray-400">{log.articles_skipped}</p>
                      <p className="text-xs text-gray-400">Ignorés</p>
                    </div>
                  </div>
                )}

                {log.errors && log.errors.length > 0 && (
                  <div className="mt-3 bg-red-50 rounded-xl p-3">
                    <p className="text-xs font-semibold text-red-600 mb-1">{log.errors.length} erreur(s)</p>
                    {log.errors.slice(0, 3).map((e, i) => (
                      <p key={i} className="text-xs text-red-500 truncate">{e}</p>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
